import type {
  TranslationOverviewStatus,
  TranslationOverviewUploadCounts,
  TranslationOverviewUploadFilter,
  TranslationOverviewUploadState,
} from './types';

export const TRANSLATION_OVERVIEW_STATUSES: Array<TranslationOverviewStatus> = [
  'Ready',
  'Pending',
  'Failed',
  'RequiresTranslation',
];

export const TRANSLATION_OVERVIEW_STATUS_LABELS: Record<TranslationOverviewStatus, string> = {
  Ready: 'Ready',
  Pending: 'Pending',
  Failed: 'Failed',
  RequiresTranslation: 'Not translated',
};

/** Offered in the upload filter. `NotReady` is omitted - that is the status filter's job. */
export const TRANSLATION_OVERVIEW_UPLOAD_FILTERS: Array<TranslationOverviewUploadFilter> = [
  'PendingUpload',
  'Uploaded',
  'UploadFailed',
];

export const TRANSLATION_OVERVIEW_UPLOAD_STATE_LABELS: Record<TranslationOverviewUploadState, string> = {
  NotReady: 'Not translated',
  PendingUpload: 'Waiting to upload',
  Uploaded: 'In Braze',
  UploadFailed: 'Upload failed',
};

export const TRANSLATION_OVERVIEW_PAGE_SIZES = [25, 50, 100];

export const TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE = 25;

/** Search fields hit the server, so wait for a pause in typing before refetching. */
export const SEARCH_DEBOUNCE_MS = 350;

/** Shown on every write control when the signed-in admin only holds the viewer role. */
export const MUTATE_BLOCKED_REASON = 'Requires the hero-planner-admin role';

/** How many missing field names a cell shows before collapsing the rest into a "+N more" suffix. */
export const MISSING_FIELDS_PREVIEW_LIMIT = 3;

export function getStatusLabel(status: TranslationOverviewStatus): string {
  return TRANSLATION_OVERVIEW_STATUS_LABELS[status] ?? status;
}

export function getUploadStateLabel(state: TranslationOverviewUploadState): string {
  return TRANSLATION_OVERVIEW_UPLOAD_STATE_LABELS[state] ?? state;
}

/**
 * A row is worth uploading when it holds pairs that have never been sent, or whose last attempt
 * failed. Retries are deliberately included: the bulk action is the only way to retry a failure.
 */
export function countUploadableRows(counts: TranslationOverviewUploadCounts): number {
  return counts.pendingUploadCount + counts.uploadFailedCount;
}

/**
 * Whether the bulk upload will do anything for this catalog.
 *
 * Readiness is judged per locale, not per catalog: a catalog sends the locales that are Ready even
 * while its siblings are still Pending or Failed. `pendingUploadCount` and `uploadFailedCount` only
 * ever count Ready locales - the API collapses a non-Ready row to `NotReady` - so a waiting row is
 * already proof that something is uploadable, and requiring `isFullyReady` on top of it would block
 * uploads the API performs happily.
 */
export function canUploadCatalog(row: TranslationOverviewUploadCounts): boolean {
  return countUploadableRows(row) > 0;
}

export function formatUploadSummary(counts: TranslationOverviewUploadCounts): string {
  const waiting = countUploadableRows(counts);
  if (waiting === 0) {
    return counts.uploadedCount > 0 ? 'All uploaded' : 'Nothing to upload';
  }

  return `${waiting.toLocaleString()} waiting`;
}

/**
 * Percentage of pairs in a row that are Ready, rounded to a whole number.
 * A row with no pairs at all reads as 0% rather than dividing by zero.
 */
export function getCoveragePercentage(readyCount: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  return Math.round((readyCount / total) * 100);
}

export function formatCoverageLabel(readyCount: number, total: number): string {
  return `${readyCount.toLocaleString()} / ${total.toLocaleString()}`;
}

export function formatMissingFields(fields: Array<string>): string {
  if (!fields.length) {
    return '—';
  }

  const preview = fields.slice(0, MISSING_FIELDS_PREVIEW_LIMIT).join(', ');
  const remaining = fields.length - MISSING_FIELDS_PREVIEW_LIMIT;

  return remaining > 0 ? `${preview} +${remaining} more` : preview;
}

/**
 * Pair count a bulk action will touch, used in the confirmation copy. Selecting catalogs acts
 * across every locale of each catalog, so the two axes multiply.
 */
export function countAffectedPairs(selectedCount: number, pairsPerSelection: number): number {
  return Math.max(selectedCount, 0) * Math.max(pairsPerSelection, 0);
}

export function formatPairCount(pairs: number): string {
  return `${pairs.toLocaleString()} ${pairs === 1 ? 'pair' : 'pairs'}`;
}
